import React, { useState, useCallback, useEffect } from 'react';
import { debounce } from 'lodash';
import { useLocalization } from '../../hooks';
import { IconRender } from '../IconRender';

interface TextFilterProps {
    label: string;
    placeholder?: string;
    onChange: (newValue: string) => void;
    data: any;
    showSearchIcon?: boolean;
    showCancelIcon?: boolean;
    searchIconPosition?: string;
    searchIconType?: '' | 'FontAwesome Icon' | 'Image';
    searchIcon?: string;
    searchImageUrl?: string;
    cancelIconType?: '' | 'FontAwesome Icon' | 'Image';
    cancelIcon?: string;
    cancelImageUrl?: string;
    defaultValue?: string;
    selectedValue?: string | null;
    clearFilters: boolean;
    staticDataTabIndex?: number;
    onClick: () => void;
}

const TextFilter = (props: TextFilterProps) => {
    const {
        label,
        placeholder,
        data,
        showSearchIcon,
        searchIconPosition,
        searchIconType,
        searchIcon,
        searchImageUrl,
        selectedValue,
        ...rest
    } = props;
    const i18n = useLocalization();
    const [value, setValue] = useState<string>(selectedValue || '');

    // Call props.onChange when user has stopped typing for half a second.
    // This prevents excess server requests while the user is typing.
    // Debouncing: https://www.freecodecamp.org/news/debounce-and-throttle-in-react-with-hooks/
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedOnChange = useCallback(
        debounce((newValue) => props.onChange(newValue), 500),
        // Create a new debounce fn when props.onChange changes
        [props.onChange]
    );

    useEffect(() => {
        if (selectedValue) {
            debouncedOnChange(selectedValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, selectedValue as any);

    useEffect(() => {
        if (rest.clearFilters) {
            setValue('');
        }
    }, [rest.clearFilters]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setValue(newValue);
        debouncedOnChange(newValue);
    };

    const handleCancel = () => {
        setValue('');
        debouncedOnChange('');
    };

    const renderSearchIcon = (position: 'Left' | 'Right') => (
        <IconRender
            iconType={searchIconType}
            iconName={searchIcon}
            iconClassName={`search-icon ${
                position === 'Left' ? 'search-icon-left' : 'search-icon-right'
            }`}
            imageUrl={searchImageUrl}
            imageClassName={`search-image  ${
                position === 'Left' ? 'search-icon-left' : 'search-icon-right'
            }`}
            imageAlt={'search'}
            staticDataTabIndex={rest.staticDataTabIndex}
        />
    );

    const renderCancelIcon = (position: 'Left' | 'Right') => (
        <IconRender
            iconType={rest.cancelIconType}
            iconName={rest.cancelIcon}
            iconClassName={`cancel-icon ${
                position === 'Left' ? 'cancel-icon-left' : 'cancel-icon-right'
            }`}
            imageUrl={rest.cancelImageUrl}
            imageClassName={`cancel-image  ${
                position === 'Left' ? 'cancel-icon-left' : 'cancel-icon-right'
            }`}
            imageAlt={'cancel'}
            staticDataTabIndex={rest.staticDataTabIndex}
            onClick={handleCancel}
        />
    );

    const renderIcon = (position: 'Left' | 'Right') => {
        if (rest.showCancelIcon && value) {
            return renderCancelIcon(position);
        } else if (!value) {
            return renderSearchIcon(position);
        }

        return null;
    };

    return (
        <div id={`search-input-${label}`} className="search-input">
            <span className="search-input-label" tabIndex={rest.staticDataTabIndex}>
                {i18n.format(label, data)}
            </span>
            <div className="search-input-box">
                {showSearchIcon && searchIconPosition === 'Left' && renderIcon('Left')}
                <input
                    className={`text-input filter-search-input ${
                        searchIconPosition === 'Left' ? 'input-left' : 'input-right'
                    }`}
                    type="text"
                    value={rest?.defaultValue || value}
                    aria-label={i18n.format(label, data)}
                    placeholder={i18n.format(placeholder, data)}
                    onChange={handleChange}
                    onClick={rest.onClick}
                />
                {showSearchIcon && searchIconPosition === 'Right' && renderIcon('Right')}
            </div>
        </div>
    );
};

export default TextFilter;
